import { Box, Button, CircularProgress, Tooltip, Typography } from "@mui/material";
import Sidebar from "./Sidebar";
import TablePanel from "./TablePanel";
import MaterialSummaryTable from "./MaterialSummaryTable";
import { LivemapState, useLivemapStore } from "./useLivemapStore";
import { useTableData } from "./useTableData";
import DispoCalendarDateSelector from "../calendar/calendarElements/DispoCalendarDateSelector";
import { TopBar } from "@/shared/structure/TopBar";
import ApiService from "@/api/ApiService";
import { attribution, logistics_day, satellite_map } from "@/shared/maps/tiles";
import { useQuery } from "@tanstack/react-query";
import L, { LatLngExpression } from "leaflet";
import { useRef, useMemo, useEffect, useCallback, useState } from "react";
import { MapContainer, LayersControl, TileLayer, Marker, LayerGroup } from "react-leaflet";
import { Autocomplete, TextField, InputAdornment } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import active_dittgen from "@/assets/icons/active_dittgen.png";
import passive_dittgen from "@/assets/icons/passive_dittgen.png";
import data_active_dittgen from "@/assets/icons/data_active_dittgen.png";
import active_juchem from "@/assets/icons/active_juchem.png";
import passive_juchem from "@/assets/icons/passive_juchem.png";
import data_active_juchem from "@/assets/icons/data_active_juchem.png";
import plant from "@/assets/icons/mixingplant.png";
import ghost_full from "@/assets/icons/ghost_full.png";
import excavator_d from "@/assets/icons/excavator_dittgen.png";
import small_d from "@/assets/icons/small_dittgen.png";
import excavator_j from "@/assets/icons/excavator_juchem.png";
import small_j from "@/assets/icons/small_juchem.png";
import ghost_empty from "@/assets/icons/ghost_empty.png";
import truck_full from "@/assets/icons/truck_full.png";
import truck_empty from "@/assets/icons/truck_empty.png";
import { useGlobalStore, usePersistantStore } from "@/stores/global";
import dayjs from "dayjs";
import { downloadCsv } from "@/shared/helpers/downloadCsv";
import { BatteryChargingFull, BluetoothAudio, CalendarMonth, FileDownload, Settings } from "@mui/icons-material";

import AccountHardHatIcon from "@mui-extra/icons/AccountHardHatIcon";
import ArmFlexIcon from "@mui-extra/icons/ArmFlexIcon";

import ConstructionIcon from "@mui/icons-material/Construction";
import BuildIcon from "@mui/icons-material/Build";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import FactoryIcon from "@mui/icons-material/Factory";
import { Group, SatelliteDish, TrafficCone } from "lucide-react";
import DeviceInformation from "../device/DeviceInformation";
import CircleIcon from "@mui/icons-material/Circle";
import { getBeaconVoltageStatus } from "@/shared/beacon/voltageStatus";
import { DEVICE_ICON_SCALE, deviceIconCompany, getDeviceIcon } from "./deviceIcons";

// Größen nach dem dargestellten Motiv, unabhängig von Tracker/Beacon oder Geräteart.
// Nicht aufgeführte Motive (z. B. Anbaugeräte und Fugenschneider) bleiben bei 0,4.
const DEVICE_ICON_SCALES: Record<string, number> = {
  "machine-control-display": 0.3,
  "hedge-trimmer": 0.3,
  "industrial-lifting-hook": 0.3,
  "handheld-disc-cutter": 0.3,
  "pipe-laying-laser": 0.3,
  "compact-mini-excavator": 0.5,
  "diesel-forklift": 0.5,
  "compact-telehandler": 0.5,
  "compact-hatchback-car": 0.5,
  "commercial-service-van": 0.5,
  "tandem-axle-equipment-trailer": 0.5,
  "towable-air-compressor": 0.5,
  "towable-water-tank": 0.5,
  "agricultural-utility-tractor": 0.5,
  "modular-site-office-container": 0.5,
  "trench-shoring-box": 0.5,
  "road-milling-machine": 0.6,
  "municipal-utility-truck": 0.6,
  "asphalt-paver": 0.6,
  "single-drum-road-roller": 0.6,
  "wheeled-excavator": 0.6,
  "crawler-excavator": 0.6,
  "articulated-site-dumper": 0.6,
  "crawler-bulldozer": 0.6,
  "construction-tipper-truck": 0.6,
  "articulated-wheel-loader": 0.6,
};

type MarkerType =
  | "site"
  | "data_active_dittgen"
  | "active_dittgen"
  | "passive_dittgen"
  | "data_active_juchem"
  | "active_juchem"
  | "passive_juchem"
  | "plant"
  | "truck"
  | "ghost"
  | "beacon"
  | "tracker"
  | "beacon_dittgen"
  | "tracker_dittgen"
  | "beacon_juchem"
  | "tracker_juchem";

const norm = (v: any) => (v ?? "").toString().trim().toLowerCase();

const normalizeQuery = (q: string) => {
  // entfernt Klammern, reduziert Whitespace
  return norm(q).replace(/[()]/g, " ").replace(/\s+/g, " ").trim();
};

const getSearchTokens = (m: any) => {
  const name = norm(m.data?.name);
  const number = norm(m.data?.number); // Trucks haben keine number -> dann leer
  const category = norm(m.data?.category); // nur bei Geräten relevant, sonst leer
  return [name, number, category].filter(Boolean);
};

const markerMatchesQuery = (m: any, q: string) => {
  const query = normalizeQuery(q);
  if (!query) return true;

  const parts = query.split(" ").filter(Boolean);
  if (!parts.length) return true;

  const tokens = getSearchTokens(m);

  // alle query-teile müssen irgendwo in den tokens vorkommen
  return parts.every((p) => tokens.some((t) => t.includes(p)));
};

type MarkerCategory =
  | "site_active_with_deliveries"
  | "site_active_without_deliveries"
  | "site_other"
  | "plant"
  | "truck"
  | "ghost"
  | "tracker"
  | "beacon";

const getMarkerCategory = (m: { type: MarkerType; icon?: string }): MarkerCategory | null => {
  // Baustellen: icon ist z.B. data_active_dittgen / active_juchem / passive_dittgen ...
  if (m.type === "site") {
    const iconType = (m.icon ?? "") as MarkerType;

    if (iconType === "data_active_dittgen" || iconType === "data_active_juchem") return "site_active_with_deliveries";
    if (iconType === "active_dittgen" || iconType === "active_juchem") return "site_active_without_deliveries";
    if (iconType === "passive_dittgen" || iconType === "passive_juchem") return "site_other";

    // falls es mal neue Site-Icons gibt, die du noch nicht zugeordnet hast
    return "site_other";
  }

  // Rest direkt über type
  if (m.type === "plant") return "plant";
  if (m.type === "truck") return "truck";
  if (m.type === "ghost") return "ghost";

  if (m.type === "tracker") return "tracker";
  if (m.type === "beacon") return "beacon";

  console.log(m.type);
  // alles andere aktuell nicht über Checkbox steuerbar
  return null;
};

const CATEGORY_LABEL: Record<MarkerCategory, string> = {
  site_active_with_deliveries: "Aktive Baustellen mit Lieferungen",
  site_active_without_deliveries: "Aktive Baustellen ohne Lieferungen",
  site_other: "Andere Baustellen",
  plant: "Mischanlagen",
  truck: "LKW",
  ghost: "Geister-LKW",
  tracker: "Tracker",
  beacon: "Beacon",
};

interface MarkerData {
  id: string;
  type: MarkerType;
  position: [number, number];
  data: any;
}

const getIconFromData = (data) => {
  const region = data.region === "wdv_juchem_DITTGEN" ? "dittgen" : "juchem";
  const pref = data.hasDeliveries ? "data_" : "";
  const state = data.active ? "active_" : "passive_";
  return pref + state + region;
};

const LivemapContainer = () => {
  const dateObj = useLivemapStore((s) => s.getDate());
  const setDate = useLivemapStore((s) => s.setDate);
  const region = usePersistantStore((s) => s.region);

  const setPageTitle = useGlobalStore((state) => state.setPageTitle);

  useEffect(() => setPageTitle("Karte"), []);

  const map = useRef<any>();
  const date = useLivemapStore((s) => s.date);
  const hasFitted = useRef(false);
  const setSelectedMarker = useLivemapStore((s) => s.setSelectedMarker);
  const selectedMarker = useLivemapStore((s) => s.selectedMarker);

  const { data } = useQuery({
    queryKey: ["mapData", date, region],
    queryFn: ApiService.getLiveMap("map", date, region),
    refetchInterval: date === getTodayDate() ? 10_000 : 60_000,
  });

  const markers = useMemo(() => {
    if (!data) return [];
    return Object.entries(data.markers).map(([id, info]: any) => ({
      id,
      type: info.type as MarkerType,
      icon:
        info.type === "site"
          ? getIconFromData(info.data)
          : info.type === "tracker" || info.type === "beacon"
            ? info.type + (info.data.region === "wdv_juchem_DITTGEN" ? "_dittgen" : "_juchem")
            : info.type,
      position: info.position as LatLngExpression,
      data: info.data,
    }));
  }, [data]);

  type SearchIconKind = "site" | "device" | "truck" | "plant" | "value";

  type SearchOption =
    | { kind: "marker"; label: string; markerId: string; iconKind: SearchIconKind }
    | { kind: "value"; label: string; iconKind: SearchIconKind };

  const optionIcon = (k: SearchIconKind) => {
    switch (k) {
      case "site":
        return <TrafficCone fontSize="small" />;
      case "device":
        return <Settings fontSize="small" />;
      case "truck":
        return <LocalShippingIcon fontSize="small" />;
      case "plant":
        return <FactoryIcon fontSize="small" />;
      default:
        return <Group fontSize="small" />;
    }
  };

  const searchOptions = useMemo<SearchOption[]>(() => {
    if (!markers.length) return [];

    const opts: SearchOption[] = [];
    const seen = new Set<string>();

    for (const m of markers) {
      const name = (m.data?.name ?? "").toString().trim();
      const number = (m.data?.number ?? "").toString().trim();
      const category = (m.data?.category ?? "").toString().trim();

      const markerCat = getMarkerCategory(m as any);
      const iconKind: SearchIconKind =
        markerCat === "plant"
          ? "plant"
          : markerCat === "truck" || markerCat === "ghost"
            ? "truck"
            : markerCat === "tracker" || markerCat === "beacon"
              ? "device"
              : m.type === "site"
                ? "site"
                : "value";

      // Marker-spezifische Vorschläge (klickbar -> selektiert Marker)
      if (name) {
        const label = number ? `${name} (${number})` : name;
        const key = `marker:${m.id}:${label}`;
        if (!seen.has(key)) {
          seen.add(key);
          opts.push({ kind: "marker", label, markerId: m.id, iconKind });
        }
      } else if (number) {
        const key = `marker:${m.id}:#${number}`;
        if (!seen.has(key)) {
          seen.add(key);
          opts.push({ kind: "marker", label: number, markerId: m.id, iconKind });
        }
      }

      // Werte-Vorschläge (nicht marker-spezifisch), z.B. Geräte-Kategorien
      if (category) {
        const key = `value:cat:${category.toLowerCase()}`;
        if (!seen.has(key)) {
          seen.add(key);
          // Kategorien sind bei euch aktuell Geräte-Kategorien -> Wrench/Build passt
          opts.push({ kind: "value", label: category, iconKind: "value" });
        }
      }
    }

    // optional: Marker zuerst, dann Values; innerhalb alphabetisch
    opts.sort((a, b) => {
      const ak = a.kind === "marker" ? 0 : 1;
      const bk = b.kind === "marker" ? 0 : 1;
      if (ak !== bk) return ak - bk;
      return a.label.localeCompare(b.label, "de");
    });

    return opts;
  }, [markers]);

  // getrennte States: UI-Text vs. Filter-Query
  const [inputValue, setInputValue] = useState("");
  const [searchQuery, setSearchQuery] = useState("");

  const filteredMarkers = useMemo(() => {
    if (!searchQuery.trim()) return markers;
    return markers.filter((m) => markerMatchesQuery(m, searchQuery));
  }, [markers, searchQuery]);

  const markersByCategory = useMemo(() => {
    const map = new Map<MarkerCategory, typeof markers>();

    for (const m of filteredMarkers) {
      const cat = getMarkerCategory(m as any);
      if (!cat) continue;
      if (!map.has(cat)) map.set(cat, []);
      map.get(cat)!.push(m);
    }

    return map;
  }, [filteredMarkers]);

  const availableCategories = useMemo(() => {
    // Reihenfolge wie gewünscht
    const order: MarkerCategory[] = [
      "site_active_with_deliveries",
      "site_active_without_deliveries",
      "site_other",
      "plant",
      "truck",
      "ghost",
      "tracker",
      "beacon",
    ];

    return order.filter((c) => (markersByCategory.get(c)?.length ?? 0) > 0);
  }, [markersByCategory]);

  useEffect(() => {
    map.current?.invalidateSize();
  }, [selectedMarker]);

  useEffect(() => {
    hasFitted.current = false;
  }, [date, region]);

  useEffect(() => {
    if (!map.current || hasFitted.current || filteredMarkers.length === 0) return;

    const bounds = L.latLngBounds(filteredMarkers.map((m) => m.position));
    map.current.fitBounds(bounds, { paddingBottomRight: [0, 0] });
    hasFitted.current = true;
    // map.current bewusst NICHT in deps (wie gewünscht)
  }, [filteredMarkers]);

  const setSelectedMarkerFromId = useCallback(
    (id, type, name) => {
      const marker = markers.find((marker) => "" + marker.id === (type !== "truck" ? (type === "plant" ? "ama" : type) : "") + id);
      console.log((type !== "truck" ? type : "") + id, markers, id, type, name);
      if (!marker) {
        setSelectedMarker(id, { type, data: { actuality: "00:00", id, name, status: "EMPTY" } }, date);
        return;
      } else {
        console.log("marker found", marker);
        setSelectedMarker(marker.id, marker, date);
        map.current.flyTo(marker.position, Math.max(map.current.getZoom(), 14), {
          animate: true,
        });
      }
    },
    [markers, date],
  );

  const { data: tableData, isLoading: isLoadingTable, isError } = useTableData(selectedMarker?.tableId ?? "", selectedMarker?.type ?? "");

  return (
    <Box
      sx={{
        flex: 1,
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        p: 0,
        background: "#eee",
      }}
    >
      <TopBar>
        <DispoCalendarDateSelector overwriteDate={dateObj} overwriteSetDate={setDate} noFuture={true} month={false} />
        <div style={{ flex: 1 }} />
        <Autocomplete
          freeSolo
          options={searchOptions}
          inputValue={inputValue}
          getOptionLabel={(opt) => (typeof opt === "string" ? opt : opt.label)}
          filterOptions={(options, state) => {
            const q = normalizeQuery(state.inputValue);

            // ✅ erst ab 1 Zeichen Vorschläge anzeigen
            if (!q || q.length < 1) return [];

            const filtered = options.filter((o) => {
              const label = typeof o === "string" ? o : o.label;
              return normalizeQuery(label).includes(q);
            });

            return filtered.slice(0, 20);
          }}
          onInputChange={(_, v, reason) => {
            // "reset" passiert z.B. nach Auswahl; wir steuern das gezielt in onChange
            setInputValue(v);

            // Beim normalen Tippen soll gefiltert werden
            if (reason === "input") {
              setSearchQuery(v);
            }
          }}
          onChange={(_, value) => {
            if (!value) return;

            // 1) Marker-Option ausgewählt -> immer clear
            if (typeof value !== "string" && value.kind === "marker") {
              const m = markers.find((x) => x.id === value.markerId);
              if (!m) return;

              setSelectedMarker?.(m.id, m, date);
              map.current?.flyTo(m.position, Math.max(map.current.getZoom(), 14), { animate: true });

              setInputValue("");
              setSearchQuery("");
              return;
            }

            // 2) Kategorie/Value ausgewählt -> NIE clear (Filter soll bleiben)
            if (typeof value !== "string" && value.kind === "value") {
              setInputValue(value.label);
              setSearchQuery(value.label);
              return;
            }

            // 3) Freier Text (string) -> wenn genau 1 Treffer, selektieren und clearen
            const q = value; // hier garantiert string

            const hits = markers.filter((m) => markerMatchesQuery(m, q));
            if (hits.length === 1) {
              const m = hits[0];
              setSelectedMarker?.(m.id, m, date);
              map.current?.flyTo(m.position, Math.max(map.current.getZoom(), 14), { animate: true });

              setInputValue("");
              setSearchQuery("");
            } else {
              setInputValue(q);
              setSearchQuery(q);
            }
          }}
          renderOption={(props, option) => {
            // MUI erwartet ein <li>
            const opt = option as any;
            const kind: SearchIconKind = typeof opt === "string" ? "value" : (opt.iconKind ?? "value");
            const label = typeof opt === "string" ? opt : opt.label;

            return (
              <li {...props}>
                <Box sx={{ display: "flex", alignItems: "center", gap: "10px" }}>
                  <Box sx={{ display: "flex", alignItems: "center", opacity: 0.85 }}>{optionIcon(kind)}</Box>
                  <Box>{label}</Box>
                </Box>
              </li>
            );
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              size="small"
              placeholder="Filtern..."
              sx={{ minWidth: 360, flex: 1 }}
              InputProps={{
                ...params.InputProps,
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />
          )}
        />
      </TopBar>

      <Box display="flex" flex={1} overflow={"hidden"}>
        {/* Left: Map */}
        <Box flex={1}>
          <MapContainer ref={map} center={[49.7668, 7.3393]} zoom={11} style={{ height: "100%", width: "100%" }}>
            {/* ✅ immer offen */}
            <LayersControl position="bottomleft" collapsed={false}>
              <LayersControl.BaseLayer checked name="Straßenkarte">
                <TileLayer attribution={attribution} url={logistics_day} />
              </LayersControl.BaseLayer>
              <LayersControl.BaseLayer name="Satellit">
                <TileLayer attribution={attribution} url={satellite_map} />
              </LayersControl.BaseLayer>

              {availableCategories.map((cat) => (
                <LayersControl.Overlay key={cat} checked name={CATEGORY_LABEL[cat]}>
                  <LayerGroup>
                    {(markersByCategory.get(cat) ?? []).map((marker) => {
                      const deviceIcon = marker.type === "tracker" || marker.type === "beacon"
                        ? getDeviceIcon(marker.data?.icon, deviceIconCompany(marker.data?.region),
                            DEVICE_ICON_SCALES[marker.data?.icon] ?? DEVICE_ICON_SCALE) : null;
                      const icon = deviceIcon
                        ? L.icon({ iconUrl: deviceIcon.url, iconSize: deviceIcon.size,
                            iconAnchor: [deviceIcon.size[0] / 2, deviceIcon.size[1] / 2] })
                        : getIcon(marker.icon ?? marker.type, marker.data?.status);

                      return (
                        <Marker
                          key={marker.id}
                          position={marker.position}
                          icon={icon}
                          eventHandlers={{
                            click: () => {
                              setSelectedMarker?.(marker.id, marker, date);
                              map.current.flyTo(marker.position, Math.max(map.current.getZoom(), 14), {
                                animate: true,
                              });
                            },
                          }}
                        />
                      );
                    })}
                  </LayerGroup>
                </LayersControl.Overlay>
              ))}
            </LayersControl>
          </MapContainer>
        </Box>

        {/* Right: Sidebar */}
        <Sidebar
          open={!!selectedMarker}
          image={selectedMarker?.data.image}
          title={
            selectedMarker?.type === "device"
              ? ""
              : selectedMarker?.data.name + (!!selectedMarker?.data.number ? " (" + selectedMarker?.data.number + ")" : "")
          }
          description={selectedMarker?.type === "device" ? "" : getDescription(selectedMarker)}
        >
          {!!selectedMarker?.data.from ? (
            <Box>
              <CalendarMonth sx={{ mr: "4px", verticalAlign: "text-top" }} fontSize="small" />
              {dayjs(selectedMarker?.data.from).format("DD.MM.YYYY")} bis {dayjs(selectedMarker?.data.to).format("DD.MM.YYYY")}
            </Box>
          ) : null}
          {!!selectedMarker?.data.manager ? (
            <Box>
              <AccountHardHatIcon sx={{ mr: "4px", verticalAlign: "text-top" }} fontSize="small" />
              {selectedMarker?.data.manager.firstName} {selectedMarker?.data.manager.name}
              {selectedMarker?.data.manager.phone.length > 0 ? (
                <span
                  style={{
                    fontSize: "12px",
                    opacity: 0.8,
                    marginLeft: "4px",
                  }}
                >
                  {selectedMarker?.data.manager.phone}
                </span>
              ) : null}
            </Box>
          ) : null}
          {!!selectedMarker?.data.polier?.name ? (
            <Box>
              <ArmFlexIcon sx={{ mr: "4px", verticalAlign: "text-top" }} fontSize="small" />
              {selectedMarker?.data.polier.name}
              {selectedMarker?.data.polier.phone.length > 0 ? (
                <span
                  style={{
                    fontSize: "12px",
                    opacity: 0.8,
                    marginLeft: "4px",
                  }}
                >
                  {selectedMarker?.data.polier.phone}
                </span>
              ) : null}
            </Box>
          ) : null}

          {selectedMarker?.tableId === "" ? (
            selectedMarker?.type === "device" ? (
              <DeviceInformation
                deviceId={selectedMarker.data.number}
                withImage={false}
                injectInfo={[
                  !!selectedMarker.data.actuality
                    ? {
                        Icon: SatelliteDish,
                        title: selectedMarker.data.actuality + " Uhr",
                        subtitle: "",
                      }
                    : null,
                  !!selectedMarker?.data.rssi
                    ? {
                        Icon: BluetoothAudio,
                        title: selectedMarker?.data.rssi + " dBm",
                        subtitle: "",
                      }
                    : null,
                  selectedMarker?.data.voltage != null
                    ? {
                        Icon: BatteryChargingFull,
                        title: (() => {
                          const voltage = Number(selectedMarker.data.voltage);
                          const status = getBeaconVoltageStatus(voltage);
                          return <>{(voltage / 1000).toString().replace(/\./, ",")} V{status && (
                            <Tooltip title={status.label}>
                              <CircleIcon tabIndex={0} aria-label={status.label} sx={{ color: status.color, fontSize: 14, ml: 1, verticalAlign: "middle" }} />
                            </Tooltip>
                          )}</>;
                        })(),
                        subtitle: "",
                      }
                    : null,
                ].filter((x) => !!x)}
              />
            ) : null
          ) : isLoadingTable ? (
            <CircularProgress size={24} />
          ) : isError ? (
            <Typography color="error">Fehler beim Laden der Tabelle</Typography>
          ) : (
            <>
              <MaterialSummaryTable material={tableData?.material ?? {}} />
              <Box mt={2}>
                <TablePanel setSelectedMarkerFromId={setSelectedMarkerFromId} type={selectedMarker?.type ?? ""} rows={tableData?.rows ?? []} />
              </Box>
              <Box sx={{ mt: 1, display: "flex", justifyContent: "flex-end" }}>
                <Button
                  variant="contained"
                  onClick={() =>
                    downloadCsv<any>([
                      [
                        [
                          {
                            date,
                            title: selectedMarker?.data.name + (!!selectedMarker?.data.number ? " (" + selectedMarker?.data.number + ")" : ""),
                          },
                        ],
                        excelTitleConfig,
                      ],
                      [Object.entries(tableData?.material), excelSumConfig],
                      [tableData?.rows, excelMainConfig],
                    ])
                  }
                  startIcon={<FileDownload />}
                >
                  Exportieren
                </Button>
              </Box>
            </>
          )}
        </Sidebar>
      </Box>
    </Box>
  );
};

export default LivemapContainer;

// 🔍 Sidebar Description Logic
function getDescription(marker: LivemapState["selectedMarker"]): string {
  if (!marker) return "";
  const { type, data } = marker;

  switch (type) {
    case "mischanlage":
      return "";
    case "baustelle":
      return "";
    case "lkw":
      return data.actuality
        ? `Der LKW war um ${data.actuality} Uhr an dieser Position.`
        : "Die Position wurde hochgerechnet – die genaue Position ist unbekannt.";
    case "device":
      return data.actuality ? `Das Gerät wurde am ${data.actuality} Uhr an dieser Position gemeldet.` : "Die genaue Position ist unbekannt.";
    default:
      return "";
  }
}

const getTodayDate = () => new Date().toISOString().split("T")[0];

const getIcon = (type: MarkerType, status?: string): L.Icon => {
  let iconUrl: string | undefined;
  let size: [number, number] | undefined;
  let anchor: [number, number] | undefined;
  let zIndex = 1;

  switch (type) {
    case "ghost":
      iconUrl = status === "EMPTY" ? ghost_empty : ghost_full;
      size = [40, status === "EMPTY" ? 28 : 30];
      zIndex = 12;
      break;
    case "truck":
      iconUrl = status === "EMPTY" ? truck_empty : truck_full;
      size = [40, status === "EMPTY" ? 28 : 30];
      zIndex = 13;
      break;
    case "passive_juchem":
      iconUrl = passive_juchem;
      size = [20, 21];
      zIndex = 7;
      break;
    case "active_juchem":
      iconUrl = active_juchem;
      size = [40, 43];
      zIndex = 8;
      break;
    case "data_active_juchem":
      iconUrl = data_active_juchem;
      size = [40, 43];
      zIndex = 9;
      break;
    case "passive_dittgen":
      iconUrl = passive_dittgen;
      size = [20, 21];
      zIndex = 7;
      break;
    case "active_dittgen":
      iconUrl = active_dittgen;
      size = [40, 43];
      zIndex = 8;
      break;
    case "data_active_dittgen":
      iconUrl = data_active_dittgen;
      size = [40, 43];
      zIndex = 9;
      break;
    case "plant":
      iconUrl = plant;
      size = [54, 52];
      zIndex = 10;
      break;
    case "tracker_juchem":
      iconUrl = excavator_j;
      size = [39, 35];
      anchor = [12, 23];
      zIndex = 14;
      break;
    case "beacon_juchem":
      iconUrl = small_j;
      size = [24, 24];
      zIndex = 15;
      break;
    case "tracker_dittgen":
      iconUrl = excavator_d;
      size = [39, 35];
      anchor = [12, 23];
      zIndex = 14;
      break;
    case "beacon_dittgen":
      iconUrl = small_d;
      size = [24, 24];
      zIndex = 15;
      break;
    default:
      // ✅ sicherer Fallback statt potenziell undefined/crash
      iconUrl = passive_dittgen;
      size = [20, 21];
      zIndex = 1;
      break;
  }

  return L.icon({
    iconUrl: iconUrl!,
    iconSize: size!,
    iconAnchor: (anchor as any) || [size![0] / 2, size![1] / 2],
    zIndex,
  });
};

const excelTitleConfig = [
  {
    header: "Datum",
    valueGetter: (row) => dayjs(row.date).format("DD.MM.YYYY"),
  },
  { header: "Titel", valueGetter: (row) => row.title },
];

const excelSumConfig = [
  { header: "Artikelnummer", valueGetter: (row) => row[1][1] },
  { header: "Artikelname", valueGetter: (row) => row[0] },
  { header: "Menge", valueGetter: (row) => (row[1][0] + "").replace(".", ",") },
  { header: "Einheit", valueGetter: (row) => row[1][2] },
];

const excelMainConfig = [
  { header: "Lieferscheinnummer", valueGetter: (row) => row.number },
  { header: "Werksnummer", valueGetter: (row) => row.plant.number },
  { header: "Werksname", valueGetter: (row) => row.plant.name },
  { header: "Baustellennummer", valueGetter: (row) => row.project.number },
  { header: "Baustellenname", valueGetter: (row) => row.project.name },
  { header: "Artikelnummer", valueGetter: (row) => row.article.number },
  { header: "Artikelname", valueGetter: (row) => row.article.name },
  { header: "Menge", valueGetter: (row) => row.tonnage.replace(".", ",") },
  { header: "Einheit", valueGetter: (row) => row.article.unit },
  { header: "LKW", valueGetter: (row) => row.truck },
  {
    header: "Beladung",
    valueGetter: (row) => dayjs.utc(row.loadedAt).local().format("DD.MM.YYYY HH:mm:ss"),
  },
  {
    header: "Ankunft Baustelle",
    valueGetter: (row) => (!row.enterBsAt ? "" : dayjs.utc(row.enterBsAt).local().format("DD.MM.YYYY HH:mm:ss")),
  },
  {
    header: "Entladebeginn",
    valueGetter: (row) => (!row.startUnloadAt ? "" : dayjs.utc(row.startUnloadAt).local().format("DD.MM.YYYY HH:mm:ss")),
  },
  {
    header: "Entladeende",
    valueGetter: (row) => (!row.endUnloadAt ? "" : dayjs.utc(row.endUnloadAt).local().format("DD.MM.YYYY HH:mm:ss")),
  },
  {
    header: "Verlassen Baustelle",
    valueGetter: (row) => (!row.leaveBsAt ? "" : dayjs.utc(row.leaveBsAt).local().format("DD.MM.YYYY HH:mm:ss")),
  },
];
